import { defer, EMPTY, expand, map, Observable } from 'rxjs';

/** What the walk needs from a page: its rows and the total of the query. */
export type TCountedPage = {
  Items: readonly unknown[];
  Total: number;
};

/**
 * Every page of a server-paginated query, one request at a time, in order —
 * the exports of the inactivation preview and of the audit list.
 *
 * Emits each page as it arrives (so the caller can report progress) and
 * completes after the page that reaches `Total`. An empty page also stops the
 * walk, so a `Total` larger than the rows that exist cannot loop.
 *
 * `fetchPage` receives the previous page: the preview needs it to cite the
 * simulation the rows came from on the next request.
 */
export function fetchAllPages<P extends TCountedPage>(
  fetchPage: (skip: number, previous: P | null) => Observable<P>
): Observable<P> {
  const page = (
    skip: number,
    previous: P | null
  ): Observable<{ page: P; next: number | null }> =>
    // `defer`: the next page is only requested once the previous answered.
    defer(() => fetchPage(skip, previous)).pipe(
      map((current) => {
        const next = skip + current.Items.length;
        return {
          page: current,
          next: current.Items.length > 0 && next < current.Total ? next : null,
        };
      })
    );

  return page(0, null).pipe(
    expand(({ page: current, next }) =>
      next === null ? EMPTY : page(next, current)
    ),
    map(({ page: current }) => current)
  );
}
